/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { lazy, Suspense } from 'react';

import { jsx } from '@compiled/react';

import type { ContentProps } from '@atlaskit/popup';
import { ATI_PRESETS } from '@atlassian/universal-create-common';

import { GenericErrorBoundary } from '../../../services';

import { FormLoading } from './Loading';

const GoalCreateWrapperAsync = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_universal-create--goal-create-wrapper" */
		'./townsquare/goal'
	).then((module) => ({
		default: module.GoalCreateWrapper,
	})),
);

const TeamCreateWrapperAsync = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_universal-create--team-create-wrapper" */
		'./townsquare/team'
	).then((module) => ({
		default: module.TeamCreateWrapper,
	})),
);

const ProjectCreateWrapperAsync = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_universal-create--project-create-wrapper" */
		'./townsquare/project'
	).then((module) => ({
		default: module.ProjectCreateWrapper,
	})),
);

function LazyLoadCreateContent({ children }: { children: JSX.Element }) {
	return (
		<GenericErrorBoundary name="lazy-load-create-content">
			<Suspense fallback={<FormLoading />}>{children}</Suspense>
		</GenericErrorBoundary>
	);
}

const CREATE_CONTENT_PRESETS = {
	[ATI_PRESETS.GOAL]: (props: ContentProps) => (
		<LazyLoadCreateContent>
			<GoalCreateWrapperAsync {...props} />
		</LazyLoadCreateContent>
	),
	[ATI_PRESETS.TEAM]: (props: ContentProps) => (
		<LazyLoadCreateContent>
			<TeamCreateWrapperAsync {...props} />
		</LazyLoadCreateContent>
	),
	[ATI_PRESETS.PROJECT]: (props: ContentProps) => (
		<LazyLoadCreateContent>
			<ProjectCreateWrapperAsync {...props} />
		</LazyLoadCreateContent>
	),
};

export function getCreateContentLoader(
	ati: string,
): ((props: ContentProps) => JSX.Element) | undefined {
	return CREATE_CONTENT_PRESETS[ati as keyof typeof CREATE_CONTENT_PRESETS];
}
